import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { fetchAuthorizedProperties } from "@/features/projects/services/projectsApi";
import { isAdminUser } from "@/shared/lib/adminUids";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { useAuth } from "@/shared/hooks/useAuth";

export type PortfolioCounts = { totalProperties: number; totalAssets: number };

// Keep property_id IN() lists within Supabase URL length limits.
const ID_CHUNK = 200;

/**
 * Header-only portfolio counts (sites + assets) for the agent dashboard.
 *
 * The full {@link usePortfolioOverview} pulls every asset/attribute/cost row to
 * compute widget aggregations — ~20s for a large client. The agent dashboard's
 * chrome needs only two numbers, so this fetches property ids (cheap) and asks
 * Supabase for an exact asset count with `head: true` — no asset rows cross the
 * wire. Scoping mirrors usePortfolioOverview: super-admins by organization_id;
 * client users via the token-scoped authorized set (already pinned to the org
 * inside fetchAuthorizedProperties).
 */
export function usePortfolioCounts(clientId?: string) {
  const { loading: authLoading } = useAuth();
  const isAdmin = isAdminUser(firebaseAuth?.currentUser?.uid);

  return useQuery<PortfolioCounts>({
    queryKey: ["portfolio-counts", clientId, isAdmin],
    queryFn: async (): Promise<PortfolioCounts> => {
      if (!clientId) throw new Error("No clientId");

      let ids: string[];
      if (isAdmin) {
        const rows: { id: string }[] = [];
        let from = 0;
        const pageSize = 1000;
        while (true) {
          const { data, error } = await supabase
            .from("properties")
            .select("id")
            .eq("organization_id", clientId)
            .range(from, from + pageSize - 1);
          if (error) throw error;
          if (data?.length) rows.push(...(data as { id: string }[]));
          if (!data || data.length < pageSize) break;
          from += pageSize;
        }
        ids = rows.map((r) => r.id);
      } else {
        ids = (await fetchAuthorizedProperties<{ id: string }>("id")).map((r) => r.id);
      }

      if (ids.length === 0) return { totalProperties: 0, totalAssets: 0 };

      // Exact asset count via parallel head counts (no rows transferred).
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += ID_CHUNK) chunks.push(ids.slice(i, i + ID_CHUNK));
      const counts = await Promise.all(
        chunks.map(async (chunk) => {
          const { count, error } = await supabase
            .from("property_assets")
            .select("id", { count: "exact", head: true })
            .in("property_id", chunk);
          if (error) throw error;
          return count ?? 0;
        }),
      );

      return {
        totalProperties: ids.length,
        totalAssets: counts.reduce((sum, c) => sum + c, 0),
      };
    },
    enabled: !!clientId && !authLoading,
    staleTime: 5 * 60 * 1000,
  });
}
